import React from 'react';
import { IDeliveryDetails } from 'src/checkout/IDeliveryDetails';
import { useGroupOrderTotals } from 'src/checkout/useGroupOrderTotals';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { Loading } from 'src/shared/design-system/Loading';
import { useSelector } from 'src/store';
import { selectCart, selectPromoCode } from 'src/store/cart/selectors';
import { Currencies, Money } from 'ts-money';
import { match } from 'ts-pattern';
import { CartItem } from '../cartUtilities';

import {
  CartTotalLine,
  DeliveryFeeLine,
  DiscountLine,
  Divider,
  FeesTaxesLine,
  GrandTotalLine,
  LineContainer,
  LineHighlight,
  OrderTotalLine,
  PromoCodeLine,
  ShefTipLine,
  SmallOrderFeeLine,
  SubtotalLine,
  TotalSavingsLine,
} from './subtotal';

interface IProps {
  isFinal: boolean;
  isEditingOrder?: boolean;
  isPickupOrder?: boolean | null;
  deliveryDetails?: IDeliveryDetails;
}

export const ConsumerOrderCartSubtotals: React.FC<IProps> = ({
  isFinal,
  isEditingOrder = false,
  isPickupOrder,
  deliveryDetails,
}) => {
  const [orderTotals, loading] = useGroupOrderTotals({
    isEditingOrder,
    orderDetails: deliveryDetails,
    isPickupOrder: isPickupOrder ?? false,
  });

  const promoContext = usePromotionalsHook();
  const lastAppliedPromo = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    return selectPromoCode(cart);
  });
  const promoCode = isEditingOrder ? lastAppliedPromo : promoContext.code?.code;
  const validPromoCode = promoContext.error ? null : promoCode;
  const promoText = promoContext.promoText ?? 'Promo';

  if (!orderTotals && loading) {
    return (
      <CartItem>
        <Loading delayMs={500} />
      </CartItem>
    );
  }

  if (!orderTotals) return null;

  const {
    subtotal: orderSubtotal,
    promoApplied,
    nonPromoDiscountApplied: discountApplied,
    salesTax,
    deliveryFee,
    serviceFee,
    grandTotal,
    shefTip,
    smallOrderFee,
    remainingToRemoveSmallOrderFee,
  } = orderTotals;

  const subtotalAfterPromo = new Money(orderSubtotal ?? 0, Currencies.USD).subtract(
    new Money(promoApplied, Currencies.USD)
  ).amount;

  const subtotalAfterDiscount = new Money(subtotalAfterPromo, Currencies.USD).subtract(
    new Money(discountApplied, Currencies.USD)
  ).amount;

  const { cartTotal, hasDiscount, orderTotal, subtotal } = match({
    validPromoCode: !!validPromoCode,
    hasDiscount: discountApplied > 0,
  })
    .with({ validPromoCode: true, hasDiscount: true }, () => ({
      cartTotal: orderSubtotal ?? 0,
      hasDiscount: true,
      subtotal: subtotalAfterPromo,
      orderTotal: subtotalAfterDiscount,
    }))
    .with({ validPromoCode: true, hasDiscount: false }, () => ({
      cartTotal: orderSubtotal ?? 0,
      hasDiscount: false,
      subtotal: subtotalAfterPromo,
      orderTotal: null,
    }))
    .with({ validPromoCode: false, hasDiscount: true }, () => ({
      cartTotal: null,
      hasDiscount: true,
      subtotal: orderSubtotal ?? 0,
      orderTotal: subtotalAfterDiscount,
    }))
    .otherwise(() => ({
      cartTotal: null,
      hasDiscount: false,
      subtotal: orderSubtotal ?? 0,
      orderTotal: null,
    }));

  return (
    <CartItem style={{ flexDirection: 'column' }}>
      <Divider />
      <TotalSavingsLine totalSavings={promoApplied + discountApplied} />
      <LineContainer>
        {cartTotal && <CartTotalLine cartTotal={cartTotal} />}

        {validPromoCode && promoApplied > 0 && (
          <>
            <PromoCodeLine promoCode={validPromoCode} promoText={promoText} promoApplied={promoApplied} />
            <Divider />
          </>
        )}

        <SubtotalLine subtotal={subtotal} />

        {hasDiscount && (
          <>
            <DiscountLine discountApplied={discountApplied} />
            <Divider />
          </>
        )}

        {orderTotal && <OrderTotalLine orderTotal={orderTotal} />}

        <DeliveryFeeLine deliveryFee={deliveryFee} />

        {!!smallOrderFee && (
          <SmallOrderFeeLine smallOrderFee={smallOrderFee} remainingToRemoveFee={remainingToRemoveSmallOrderFee} />
        )}

        <FeesTaxesLine isFinal={isFinal} salesTax={salesTax} serviceFee={serviceFee} />

        <ShefTipLine shefTip={shefTip} />

        <LineHighlight>
          <GrandTotalLine grandTotal={grandTotal ?? 0} />
        </LineHighlight>
      </LineContainer>
    </CartItem>
  );
};
